import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import CheckBack from "../../components/CheckBack";
import Modal from "../../components/Modal";
import WaitAsync from "../../components/WaitAsync";

// ── Acceso al portal de clientes (ago-2026, solo admin, solo clientes) ────
// Sección quirúrgica al final de la ficha: crear/regenerar la credencial del
// portal externo (portal.binimeliz), ver estado y último acceso, activar o
// desactivar. La clave viaja UNA vez al server (se cifra allá) y no se puede
// volver a leer: al regenerar se define una nueva.
const claveAleatoria = () => {
  const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  const buf = new Uint32Array(12);
  window.crypto.getRandomValues(buf);
  for (let i = 0; i < 12; i++) out += abc[buf[i] % abc.length];
  return out;
};

const fechaCL = v => (v ? new Date(v).toLocaleString("es-CL") : "Nunca");

const PortalAccessSection = ({ entity }) => {
  const [cred, setCred] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ email: "", nombreContacto: "", clave: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const cargar = async () => {
    try {
      const rows = (await backend.get("portal-admin/")).data || [];
      setCred(rows.find(r => (r.entity?._id || r.entity) === entity._id) || null);
    } catch (err) {
      console.log(err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity._id]);

  const abrirForm = () => {
    setForm({
      email: cred?.email || entity.email || "",
      nombreContacto: cred?.nombreContacto || entity.contactPerson || "",
      clave: "",
    });
    setMsg("");
    setEditando(true);
  };

  const guardar = async () => {
    if (busy) return;
    setMsg("");
    if (!form.clave || form.clave.length < 8) {
      setMsg("La clave debe tener al menos 8 caracteres (usa Generar).");
      return;
    }
    setBusy(true);
    try {
      await backend.post("portal-admin/credencial", {
        entityId: entity._id,
        email: form.email.trim(),
        clave: form.clave,
        nombreContacto: form.nombreContacto.trim(),
      });
      setMsg(
        `Credencial guardada. Entregue al cliente: ${form.email.trim()} / ${form.clave} — no se puede volver a ver.`
      );
      setEditando(false);
      await cargar();
    } catch (err) {
      setMsg(err.response?.data?.error || "No se pudo guardar la credencial");
    } finally {
      setBusy(false);
    }
  };

  const toggleActivo = async () => {
    if (busy || !cred) return;
    setBusy(true);
    setMsg("");
    try {
      await backend.patch(`portal-admin/${cred._id}/activo`, { activo: !cred.activo });
      await cargar();
    } catch (err) {
      setMsg(err.response?.data?.error || "No se pudo cambiar el estado");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = { width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid rgba(128,128,128,0.35)" }}>
      <div className="bini-section-title" style={{ fontWeight: 600, marginBottom: 8 }}>
        Acceso al portal
      </div>

      {cargando ? (
        <p style={{ opacity: 0.7 }}>Cargando…</p>
      ) : (
        <>
          {cred ? (
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div>
                {cred.activo ? "🟢 Activo" : "⛔ Desactivado"} — {cred.email}
                {cred.nombreContacto ? ` (${cred.nombreContacto})` : ""}
              </div>
              <div style={{ opacity: 0.75 }}>Último acceso: {fechaCL(cred.ultimoAcceso)}</div>
            </div>
          ) : (
            <p style={{ fontSize: 13, opacity: 0.75 }}>Este cliente no tiene acceso al portal.</p>
          )}

          {editando ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              <div className="bini-field">
                <label>Email de acceso</label>
                <input
                  className="bini-input"
                  style={inputStyle}
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="bini-field">
                <label>Nombre de contacto</label>
                <input
                  className="bini-input"
                  style={inputStyle}
                  value={form.nombreContacto}
                  onChange={e => setForm({ ...form, nombreContacto: e.target.value })}
                />
              </div>
              <div className="bini-field">
                <label>Clave (mínimo 8 caracteres)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="bini-input"
                    style={{ ...inputStyle, flex: 1 }}
                    value={form.clave}
                    onChange={e => setForm({ ...form, clave: e.target.value })}
                  />
                  <button type="button" onClick={() => setForm({ ...form, clave: claveAleatoria() })}>
                    Generar
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" disabled={busy} onClick={guardar}>
                  {cred ? "Regenerar credencial" : "Crear credencial"}
                </button>
                <button type="button" disabled={busy} onClick={() => setEditando(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button type="button" disabled={busy} onClick={abrirForm}>
                {cred ? "Regenerar credencial" : "Crear credencial"}
              </button>
              {cred && (
                <button type="button" disabled={busy} onClick={toggleActivo}>
                  {cred.activo ? "Desactivar acceso" : "Activar acceso"}
                </button>
              )}
            </div>
          )}

          {msg && <p style={{ fontSize: 12, marginTop: 8, wordBreak: "break-word" }}>{msg}</p>}
        </>
      )}
    </div>
  );
};

const Entity = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef();
  const [entity, setEntity] = useState();
  const authContext = useContext(AuthContext);
  const puedeEditarEntidades = authContext?.can?.("editar_entidades") || false;

  const getEntity = async () => {
    try {
      setEntity((await backend.get(`entity/${location.state?._id}`)).data);
    } catch (err) {
      console.log(err);
    }
  };

  const getType = type => {
    switch (type) {
      case "client":
        return "Cliente";
      case "provider":
        return "Proveedor";
      default:
        return "";
    }
  };

  const goBack = async () => {
    await new Promise(r => setTimeout(r, 100));
    navigate(-1);
  };

  return (
    <CheckBack to="/entities" onShow={() => ref.current?.show()}>
      <Modal
        ref={ref}
        title={entity?.name || ""}
        hideCancel
        outsideClose
        onHide={goBack}
      >
        <WaitAsync func={getEntity}>
          {entity?.email && <p>{entity?.email}</p>}
          {entity?.phone && <p>{entity?.phone}</p>}
          {entity?.rut && <p>{entity?.rut}</p>}
          {entity?.type && <p>{getType(entity?.type)}</p>}
          {puedeEditarEntidades && entity && ["client", "both"].includes(entity.type) && (
            <PortalAccessSection entity={entity} />
          )}
        </WaitAsync>
      </Modal>
    </CheckBack>
  );
};

export default Entity;
